import Phaser from "phaser";
import { pixelRatio } from "../display";
import { lightColor, TRICK_COLOR } from "../palette";
import { GLOW_SIZE, TEX } from "./textures";

const MISS_COLOR = 0xff4d4d;

/** Short-lived feedback: a burst and "+N" on a collect, a ripple wherever a finger lands. */
export class Effects {
	private readonly bursts = new Map<number, Phaser.GameObjects.Particles.ParticleEmitter>();

	constructor(private readonly scene: Phaser.Scene) {}

	collected(
		x: number,
		y: number,
		color: number,
		golden: boolean,
		points: number,
		size: number,
	): void {
		const tint = lightColor(color, golden);
		this.emitter(tint).explode(golden ? 40 : 18, x, y);
		this.ring(x, y, tint, size * 2.5, 450);
		const px = pixelRatio();
		const fontSize = Math.round(Math.max(28 * px, Math.min(72 * px, size * 1.4)));
		const text = this.scene.add
			.text(x, y, `+${points}`, {
				fontFamily: "system-ui, sans-serif",
				fontSize: `${fontSize}px`,
				fontStyle: "bold",
				color: Phaser.Display.Color.ValueToColor(tint).rgba,
				stroke: "#000000",
				strokeThickness: Math.max(4 * px, fontSize / 8),
			})
			.setOrigin(0.5);
		this.scene.tweens.add({
			targets: text,
			y: y - fontSize * 1.6,
			alpha: { from: 1, to: 0 },
			scale: { from: 0.6, to: 1.2 },
			duration: 800,
			ease: "Cubic.easeOut",
			onComplete: () => text.destroy(),
		});
	}

	/** A caught bat or ghost: a burst in its colour and "+5". */
	caught(x: number, y: number, points: number, size: number, color: number): void {
		this.emitter(color).explode(36, x, y);
		this.emitter(0xffd700).explode(14, x, y);
		this.ring(x, y, color, size * 2.5, 500);
		this.floatText(x, y, `+${points}`, color, Math.max(36 * pixelRatio(), size * 0.6), -1);
	}

	/** A trick light tapped: a puff of green and what it cost ("−2"; nothing at 0 points). */
	tricked(x: number, y: number, penalty: number, size: number): void {
		this.emitter(TRICK_COLOR).explode(24, x, y);
		this.ring(x, y, TRICK_COLOR, size * 2, 400);
		if (penalty > 0) {
			this.floatText(x, y, `−${penalty}`, TRICK_COLOR, Math.max(34 * pixelRatio(), size), 1);
		}
	}

	/** A floating number: up (dir −1) for gains, sinking (dir 1) for losses. */
	private floatText(
		x: number,
		y: number,
		text: string,
		tint: number,
		fontSize: number,
		dir: -1 | 1,
	): void {
		const size = Math.round(fontSize);
		const label = this.scene.add
			.text(x, y, text, {
				fontFamily: "system-ui, sans-serif",
				fontSize: `${size}px`,
				fontStyle: "bold",
				color: Phaser.Display.Color.ValueToColor(tint).rgba,
				stroke: "#000000",
				strokeThickness: Math.max(4 * pixelRatio(), size / 8),
			})
			.setOrigin(0.5)
			.setDepth(10);
		this.scene.tweens.add({
			targets: label,
			y: y + dir * size * 1.5,
			alpha: { from: 1, to: 0 },
			scale: { from: 0.7, to: 1.2 },
			duration: 850,
			ease: dir < 0 ? "Cubic.easeOut" : "Cubic.easeIn",
			onComplete: () => label.destroy(),
		});
	}

	/** Minuspoäng: a red "−1" sinks from a tap that hit nothing (nothing when it cost 0). */
	missed(x: number, y: number, penalty: number): void {
		if (penalty <= 0) return;
		const px = pixelRatio();
		this.ring(x, y, MISS_COLOR, 70 * px, 300);
		const text = this.scene.add
			.text(x, y, `−${penalty}`, {
				fontFamily: "system-ui, sans-serif",
				fontSize: `${34 * px}px`,
				fontStyle: "bold",
				color: Phaser.Display.Color.ValueToColor(MISS_COLOR).rgba,
				stroke: "#000000",
				strokeThickness: 5 * px,
			})
			.setOrigin(0.5)
			.setDepth(10);
		this.scene.tweens.add({
			targets: text,
			y: y + 50 * px,
			alpha: { from: 1, to: 0 },
			duration: 700,
			ease: "Cubic.easeIn",
			onComplete: () => text.destroy(),
		});
	}

	/** Every press shows where it landed, hit or not; `size` in CSS pixels. */
	ripple(x: number, y: number, size: number): void {
		this.ring(x, y, 0xffffff, size * pixelRatio(), 300, 0.5);
	}

	private ring(x: number, y: number, tint: number, size: number, ms: number, alpha = 0.9): void {
		const ring = this.scene.add
			.image(x, y, TEX.ring)
			.setBlendMode(Phaser.BlendModes.ADD)
			.setTint(tint)
			.setScale((size * 0.2) / GLOW_SIZE)
			.setAlpha(alpha);
		this.scene.tweens.add({
			targets: ring,
			scale: size / GLOW_SIZE,
			alpha: 0,
			duration: ms,
			ease: "Cubic.easeOut",
			onComplete: () => ring.destroy(),
		});
	}

	private emitter(tint: number): Phaser.GameObjects.Particles.ParticleEmitter {
		let emitter = this.bursts.get(tint);
		if (!emitter) {
			emitter = this.scene.add.particles(0, 0, TEX.spark, {
				speed: { min: 120, max: 380 },
				lifespan: { min: 400, max: 800 },
				scale: { start: 1.1, end: 0 },
				alpha: { start: 1, end: 0 },
				gravityY: 300,
				tint,
				blendMode: Phaser.BlendModes.ADD,
				emitting: false,
			});
			emitter.setDepth(10);
			this.bursts.set(tint, emitter);
		}
		return emitter;
	}
}
